import React, { useEffect, useState } from 'react';
import styles from './providerSelectionPage.module.scss';
import { useTranslation } from 'react-i18next';
import { AccountType } from '../../types/AccountType';
import { useSelectedBank, useSelectedProvider } from '../../hooks/zustandHooks';
import Container from '../container/Container';
import i18n from '../../i18n';
import BankListRow from '../bankSelect/BankListRow';
import BackButton from '../buttons/BackButton';
import { exitApp } from '../../hooks/utils';
import { IProviderDto } from '../../domain/dto/IProviderDto';
import AccountTypeToggle from './AccountTypeToggle';
import { trackEvent } from '../../App';
import { EventTypes } from '../../utils/applicationInsights';
import { useSettings } from '../../hooks/queryHooks';

const getDisplayName = function (provider: IProviderDto, accountType: AccountType) {
  if (provider.hasDisplayName) return provider.name;

  const supportedAccounts =
    accountType === AccountType.Business ? provider.supportedAccounts?.Business : provider.supportedAccounts?.Personal;

  return supportedAccounts?.map(p => (Array.isArray(p) ? p.forEach(i18n.t(p)) : i18n.t(p))).join(', ') || '';
};

const ProviderSelectionPage = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [providerType, setProviderType] = useState(AccountType.Personal);
  const setSelectedProvider = useSelectedProvider(state => state.setSelectedProvider);
  const bank = useSelectedBank(state => state.selectedBank);

  useEffect(() => {
    if (window.hj) {
      window.hj('tagRecording', ['Screen: Select Bank Account']);
    }
  }, []);

  const personalProviders =
    bank?.providers
      .filter(p => p.supportedAccounts?.Personal && p.supportedAccounts.Personal.length > 0)
      .map(provider => ({ ...provider })) || [];
  const businessProviders =
    bank?.providers
      .filter(p => p.supportedAccounts?.Business && p.supportedAccounts.Business.length > 0)
      .map(provider => ({ ...provider })) || [];

  const providers = providerType === AccountType.Business ? businessProviders : personalProviders;

  return (
    <Container
      {...(settings?.redirectUrl && {
        secondaryBtn: {
          text: t('Abort & return to partner'),
          onClick: () => exitApp()
        }
      })}
    >
      <BackButton />
      <div data-testid="provider-selection-page-container">
        <h1 className={styles.heading}>{t('Select your account')}</h1>
        <AccountTypeToggle providerType={providerType} onChange={setProviderType} />
        <div>
          {providers && providers.length > 0 && (
            <div data-testid={`${providerType}_providers`}>
              {providers.map(provider => {
                return (
                  <BankListRow
                    label={getDisplayName(provider, providerType)}
                    key={provider.providerId}
                    logoUrl={bank?.logoUrl}
                    altText={bank?.name}
                    onClick={() => {
                      setSelectedProvider({ ...provider, selectedAccountType: providerType });
                      trackEvent({
                        type: EventTypes.COMPLETE_PROVIDER_SELECTION,
                        payload: { providerId: provider?.providerId }
                      });
                    }}
                    to="/consent"
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default ProviderSelectionPage;
